/**
 * ServiceCard — Compact info card for a service.
 * Used in card grid view and Kanban board columns.
 */
import React from 'react';
import {
  PencilSquareIcon,
  TrashIcon,
  ArrowTopRightOnSquareIcon,
  LinkIcon,
  ServerStackIcon,
  UserGroupIcon,
  BuildingOfficeIcon,
} from '@heroicons/react/24/outline';
import './ServiceCard.css';

const STATUS_COLORS = {
  running:     { bg: '#dcfce7', color: '#15803d', label: 'Running' },
  stopped:     { bg: '#fee2e2', color: '#991b1b', label: 'Stopped' },
  maintenance: { bg: '#fef9c3', color: '#854d0e', label: 'Mantenimiento' },
  degraded:    { bg: '#ffedd5', color: '#9a3412', label: 'Degradado' },
  unknown:     { bg: '#f1f5f9', color: '#64748b', label: 'Desconocido' },
};

const CRITICALITY_COLORS = {
  critical: { bg: '#fee2e2', color: '#991b1b', label: 'Crítico' },
  high:     { bg: '#ffedd5', color: '#9a3412', label: 'Alto' },
  medium:   { bg: '#fef9c3', color: '#854d0e', label: 'Medio' },
  low:      { bg: '#dbeafe', color: '#1e40af', label: 'Bajo' },
};

const CARD_STYLE = {
  background: 'var(--bg-card, #fff)',
  border: '1px solid var(--border, #e2e8f0)',
  borderRadius: 10,
  padding: '12px 14px',
  cursor: 'pointer',
  transition: 'box-shadow .15s, border-color .15s',
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
};

export default function ServiceCard({ service, onEdit, onDelete, compact }) {
  const statusInfo = STATUS_COLORS[service.status] || STATUS_COLORS.unknown;
  const critInfo = CRITICALITY_COLORS[service.criticality];

  const cardStyle = {
    ...CARD_STYLE,
    padding: compact ? '10px 12px' : '12px 14px',
    gap: compact ? 6 : 8,
  };

  return (
    <div
      style={cardStyle}
      className="svc-card-hover"
      onClick={() => onEdit && onEdit()}
      onMouseEnter={e => {
        e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,.08)';
        e.currentTarget.style.borderColor = 'var(--accent, #2563eb)';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.boxShadow = '';
        e.currentTarget.style.borderColor = 'var(--border, #e2e8f0)';
      }}
    >
      {/* Top row: name + actions */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 6 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: compact ? '0.82rem' : '0.88rem', lineHeight: 1.3, color: 'var(--text-primary, #1e293b)' }}>
            {service.display_name || service.name}
          </div>
          {!compact && (service.display_name && service.display_name !== service.name) && (
            <div style={{ fontSize: '0.7rem', color: 'var(--text-muted, #94a3b8)', marginTop: 1 }}>
              {service.name}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 2, flexShrink: 0 }} onClick={e => e.stopPropagation()}>
          {service.url && (
            <button
              className="svc-card-action"
              title="Abrir URL"
              onClick={() => window.open(service.url, '_blank')}
              style={actionBtnStyle}
            >
              <ArrowTopRightOnSquareIcon style={{ width: 13, height: 13 }} />
            </button>
          )}
          <button
            className="svc-card-action"
            title="Editar"
            onClick={() => onEdit && onEdit()}
            style={actionBtnStyle}
          >
            <PencilSquareIcon style={{ width: 13, height: 13 }} />
          </button>
          <button
            className="svc-card-action svc-card-action--delete"
            title="Eliminar"
            onClick={() => onDelete && onDelete()}
            style={{ ...actionBtnStyle, color: '#ef4444' }}
          >
            <TrashIcon style={{ width: 13, height: 13 }} />
          </button>
        </div>
      </div>

      {/* Badges row */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, alignItems: 'center' }}>
        {/* Status */}
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: 4,
          padding: '1px 8px', borderRadius: 10, fontSize: '0.7rem', fontWeight: 600,
          background: statusInfo.bg, color: statusInfo.color,
        }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: statusInfo.color, flexShrink: 0 }} />
          {service.status_label || statusInfo.label}
        </span>
        {/* Criticality */}
        {critInfo && (
          <span style={{
            display: 'inline-block', padding: '1px 7px', borderRadius: 10, fontSize: '0.68rem', fontWeight: 600,
            background: critInfo.bg, color: critInfo.color,
          }}>
            {service.criticality_label || critInfo.label}
          </span>
        )}
        {/* Type */}
        <span style={{
          display: 'inline-block', padding: '1px 7px', borderRadius: 10, fontSize: '0.68rem',
          background: 'var(--bg-toolbar)', color: 'var(--text-secondary)',
        }}>
          {service.service_type_label || service.service_type || '—'}
        </span>
        {/* Access points */}
        {(service.access_point_count > 0) && (
          <span style={{ fontSize: '0.7rem', color: '#7dd3fc', display: 'inline-flex', alignItems: 'center', gap: 3 }}>
            <LinkIcon style={{ width: 11, height: 11 }} />
            {service.access_point_count}
          </span>
        )}
      </div>

      {/* Group chips */}
      {Array.isArray(service.groups) && service.groups.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {service.groups.map(g => (
            <span key={g.group_id} title={g.role ? `${g.group_name} · ${g.role}` : g.group_name}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 3,
                padding: '1px 7px', borderRadius: 10, fontSize: '0.66rem', fontWeight: 600,
                background: 'rgba(13,148,136,.12)', color: '#0d9488',
                border: '1px solid rgba(13,148,136,.25)',
              }}>
              <span>{g.group_icon}</span>
              {g.group_name}
            </span>
          ))}
        </div>
      )}

      {/* Info rows (only in non-compact mode) */}
      {!compact && (
        <>
          {/* Vendor / Version */}
          {(service.vendor || service.version) && (
            <div style={{ display: 'flex', gap: 12, fontSize: '0.72rem', color: 'var(--text-muted, #64748b)' }}>
              {service.vendor && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                  <BuildingOfficeIcon style={{ width: 11, height: 11 }} />
                  {service.vendor}
                </span>
              )}
              {service.version && <span>v{service.version}</span>}
            </div>
          )}

          {/* Hosting + Team */}
          <div style={{ display: 'flex', gap: 12, fontSize: '0.72rem', color: 'var(--text-muted, #64748b)', flexWrap: 'wrap' }}>
            {service.hosting_type_label && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                <ServerStackIcon style={{ width: 11, height: 11 }} />
                {service.hosting_type_label}
              </span>
            )}
            {service.owner_team && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                <UserGroupIcon style={{ width: 11, height: 11 }} />
                {service.owner_team}
              </span>
            )}
          </div>

          {/* Description snippet */}
          {service.description && (
            <div style={{
              fontSize: '0.7rem', color: 'var(--text-muted, #94a3b8)',
              lineHeight: 1.4, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}>
              {service.description}
            </div>
          )}
        </>
      )}
    </div>
  );
}

const actionBtnStyle = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  width: 24, height: 24, borderRadius: 5, border: 'none',
  background: 'transparent', color: '#94a3b8', cursor: 'pointer',
};
